import { Skeleton } from 'antd';
import { useState } from 'react';

import { useBookings } from '@/api/bookings';
import { useCashPosition, useDueExpenses } from '@/api/cash';
import { dueSummary } from '@core/cash/recurring';
import { useGarmentJobs } from '@/api/garmentJobs';
import { usePendingInspections } from '@/api/inspections';
import { usePayables, useReceivables } from '@/api/outstanding';
import { Button, Card, EmptyState, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { hueClasses, type Hue } from '@/components/hues';
import { formatDate, toDayjs, toIsoDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import { dayAfter, filtersForView, takeBookingIntent } from '@core/bookings/registerViews';
import { confidenceOf, headlineLabel } from '@core/cash/position';

import { can, canAct, hasFeature } from '@core/session/capability';
import {
  countChips,
  opensBooking,
  tomorrowSummary,
  workList,
  workSections,
  type CountChip,
  type WorkSectionId,
} from '@core/today/workList';
import type { Session } from '@core/session/types';
import {
  busiestLine,
  dayFigures,
  daySentence,
  inFilter,
  moneyInByAccount,
  weekAhead,
  workFilters,
  type WorkFilter,
} from '@core/today/dashboard';
import {
  bookingRows,
  garmentRows,
  inspectionRows,
  jobRows,
  moreLabel,
  movementIn,
  outstandingJobs,
  pullLimit,
  rowLimit,
  tradingPeriodLabels,
  tradingPeriods,
  visibleCards,
  visibleMoneyPanels,
  visibleTomorrowLists,
  type MoneyPanel,
  type TodayCard,
  type TodayRow,
  type TradingPeriods,
} from '@core/today/today';

/**
 * Where a shop starts its day.
 *
 * <p>
 * The app opened on Branches, a Setup master, because there was no dashboard for it to mean
 * instead — three files carried that comment. This is the screen those comments were waiting for,
 * and it is the other half of the landing fix: `shell/landing.ts` stopped sending people to a
 * screen they could not see, and this gives everyone one worth seeing.
 * </p>
 *
 * <p>
 * <strong>Counts lead and rows follow.</strong> The number is the answer — "four collections, one
 * late" — and the names underneath are only there so somebody can recognise the morning before
 * deciding to open anything. Every card is a link to the screen that already does the work; none of
 * them is a place to act, deliberately, because a second way to issue a garment is a second thing
 * to keep correct.
 * </p>
 *
 * <p>
 * <strong>Five or six small queries rather than one summary endpoint.</strong> They are the queries
 * the register and the two queues already make, so they arrive warm from the cache and a shop that
 * clicks through pays nothing twice. A server-side summary is the optimisation to reach for when a
 * chain's volume asks for it, not before.
 * </p>
 */
export function TodayPage({
  session,
  onOpen,
}: {
  session: Session;
  /** Opens a screen — on a saved view, or (for a booking row) on that one booking. */
  onOpen: (key: string, view?: string, find?: string, record?: number) => void;
}) {
  const branchId = session.branch?.id;

  // The shop's date, fixed for this mounting, exactly as the register fixes it — a laptop with a
  // wrong clock must not file today's collections under yesterday, and a date that changed under a
  // running screen would silently re-query everything at midnight.
  const [today] = useState(() => toIsoDate(new Date()) ?? '');

  const cards = visibleCards(session);
  const shows = (id: string) => cards.some((card) => card.id === id);

  /*
    Each query is asked only when its own card is visible.

    `enabled` rather than a conditional hook, which React forbids — and it is doing real work: an
    accountant holds no `CleaningJob:View`, so asking anyway would spend a request per morning on a
    403 nobody reads, and the failed query would put an error state on a screen that is meant to
    open clean.
  */
  const pickups = useBookings(shows('pickups') ? branchId : undefined, {
    ...filtersForView('pickups', today),
    pageSize: rowLimit,
  });
  const returns = useBookings(shows('returns') ? branchId : undefined, {
    ...filtersForView('returns', today),
    pageSize: rowLimit,
  });
  const overdue = useBookings(shows('overdue') ? branchId : undefined, {
    ...filtersForView('overdue', today),
    pageSize: rowLimit,
  });

  const inspections = usePendingInspections();
  const cleaning = useGarmentJobs('Cleaning');
  const alterations = useGarmentJobs('Alteration');

  const openCleaning = outstandingJobs(cleaning.data ?? []);
  const openAlterations = outstandingJobs(alterations.data ?? []);
  const dayAfterToday = dayAfter(today);

  /*
    Tomorrow's two pull-lists.

    A full page rather than `pullLimit` rows, because the unit here is the *garment* and the page is
    of bookings: one party of eight arrives as a single row. The cap is honest rather than exact —
    past 25 bookings collecting in one day the "and N more" line understates, and the tab the card
    opens is what carries the truth. A shop that big is a chain branch, and the summary endpoint
    this screen has so far refused to build is the answer then rather than a larger page now.
  */
  const lists = visibleTomorrowLists(session);
  const listing = (id: string) => lists.some((list) => list.id === id);

  const outTomorrow = useBookings(listing('pickupsTomorrow') ? branchId : undefined, {
    ...filtersForView('pickupsTomorrow', today),
  });
  const backTomorrow = useBookings(listing('returnsTomorrow') ? branchId : undefined, {
    ...filtersForView('returnsTomorrow', today),
  });

  const outRows = garmentRows(outTomorrow.data?.items ?? [], 'out');
  const backRows = garmentRows(backTomorrow.data?.items ?? [], 'back');

  /*
    The volume figures, one query per window.

    Three requests to read three integers, which is the trade this screen already makes and states:
    they go through the register's own endpoint, so each count obeys exactly the branch scoping,
    permission and stage rules of the list a person opens next, and a summary endpoint would be a
    second implementation free to disagree with it. `pageSize: 1` because only `total` is read.
  */
  const periods = tradingPeriods(today);
  const takenVisible = can(session, 'RentalOrder:View') && hasFeature(session, 'RentalOrders');
  const takenBranch = takenVisible ? branchId : undefined;

  // Written out one per window rather than mapped over `tradingPeriodLabels`, because a hook called
  // from a loop or a helper is the rule React actually enforces — and a lint suppression saying "the
  // order is fixed here" is a promise the next edit to that array quietly breaks.
  const takenToday = useBookings(takenBranch, {
    takenFrom: periods.today.from,
    takenTo: periods.today.to,
    pageSize: 1,
  });
  const takenWeek = useBookings(takenBranch, {
    takenFrom: periods.week.from,
    takenTo: periods.week.to,
    pageSize: 1,
  });
  const takenMonth = useBookings(takenBranch, {
    takenFrom: periods.month.from,
    takenTo: periods.month.to,
    pageSize: 1,
  });

  const [period, setPeriod] = useState<keyof TradingPeriods>('week');
  const [picked, setPicked] = useState<WorkFilter>('all');

  /*
    The week ahead's bars: one read of the bookings picked up over the next seven days — the phone's
    week strip (`list: 'Upcoming'` with a day range), so both count the same bookings — grouped by
    pickup day here (core `weekAhead`). A page of 100 bookings is honest rather than exact, the
    pull-lists' trade above: a branch busier than that is the chain the summary endpoint waits for.
  */
  const ahead = useBookings(shows('pickups') ? branchId : undefined, {
    list: 'Upcoming',
    dayFrom: dayAfterToday,
    dayTo: toDayjs(today)?.add(7, 'day').format('YYYY-MM-DD'),
    pageSize: 100,
  });

  const taken: Record<keyof TradingPeriods, TakenFigure> = {
    today: {
      total: takenToday.data?.total,
      loading: takenToday.isLoading,
      failed: takenToday.isError,
    },
    week: {
      total: takenWeek.data?.total,
      loading: takenWeek.isLoading,
      failed: takenWeek.isError,
    },
    month: {
      total: takenMonth.data?.total,
      loading: takenMonth.isLoading,
      failed: takenMonth.isError,
    },
  };

  /*
    The money half of the morning — the drawer, and what the shop has spent out of it.

    Each panel is asked for only when its own grant allows it, the rule every card above follows.
    It matters more here than anywhere else on the screen: `/api/cash` is one of the two endpoints
    in the product enforcing `ViewFinancials`, so asking on behalf of a counter clerk would spend a
    403 every morning and paint an error onto a screen that is meant to open clean.
  */
  const panels = visibleMoneyPanels(session);
  const showsMoney = (id: MoneyPanel['id']) => panels.some((panel) => panel.id === id);

  /*
    One request for the whole cash panel, asked over today.

    The position endpoint nets each account server-side, so `closing` is the balance as at the end
    of today — everything carried in, plus today's movement — while `in` and `out` are today's
    alone. That is why this is a windowed read rather than two: a second query for the balance
    would see a different instant, and a payment landing between them would make the three figures
    disagree by exactly one movement. It also shares a cache key with the cash screen's own
    "Today", so clicking through pays nothing twice.
  */
  const position = useCashPosition(showsMoney('cash') ? branchId : undefined, today, today);
  const moved = movementIn(position.data?.accounts);

  // Both outstanding lists, read only for somebody the panel is shown to — the same reads the
  // Outstanding screen makes, so its figures and these agree.
  const receivables = useReceivables(branchId, showsMoney('outstanding'));
  const payables = usePayables(branchId, showsMoney('outstanding'));
  const payNow = payables.data
    ? payables.data.ageing.upTo30 +
      payables.data.ageing.upTo60 +
      payables.data.ageing.upTo90 +
      payables.data.ageing.over90
    : 0;

  const spendBranch = showsMoney('expenses') ? branchId : undefined;

  const dueExpenses = useDueExpenses(spendBranch);
  const dueLine = dueSummary(dueExpenses.data ?? [], periods.today.from);

  const data: Record<string, CardData> = {
    pickups: {
      count: pickups.data?.total,
      rows: bookingRows(pickups.data?.items ?? []),
      loading: pickups.isLoading,
      failed: pickups.isError,
    },
    returns: {
      count: returns.data?.total,
      rows: bookingRows(returns.data?.items ?? []),
      loading: returns.isLoading,
      failed: returns.isError,
    },
    overdue: {
      count: overdue.data?.total,
      // Every row on this card is late by definition, so each carries the reserved colour rather
      // than the card having to say it once and leave the rows looking ordinary.
      rows: bookingRows(overdue.data?.items ?? [], { flagged: true }),
      loading: overdue.isLoading,
      failed: overdue.isError,
    },
    inspection: {
      count: inspections.data?.length,
      rows: inspectionRows(inspections.data ?? []),
      loading: inspections.isLoading,
      failed: inspections.isError,
    },
    cleaning: {
      count: openCleaning.length,
      rows: jobRows(openCleaning, today),
      loading: cleaning.isLoading,
      failed: cleaning.isError,
    },
    alterations: {
      count: openAlterations.length,
      rows: jobRows(openAlterations, today),
      loading: alterations.isLoading,
      failed: alterations.isError,
    },
    /*
      The pull-lists count garments, not bookings, so `count` is the row total rather than the
      page's `total` — which is the number of *bookings* and would put "3" over a list of nine
      garments. `moreLabel` is then measuring the same unit at both ends.
    */
    pickupsTomorrow: {
      count: outRows.length,
      rows: outRows,
      loading: outTomorrow.isLoading,
      failed: outTomorrow.isError,
    },
    returnsTomorrow: {
      count: backRows.length,
      rows: backRows,
      loading: backTomorrow.isLoading,
      failed: backTomorrow.isError,
    },
  };

  const dataFor = (id: string): CardData => data[id] ?? { rows: [], loading: false, failed: false };

  const anyLoading = cards.some((card) => dataFor(card.id).loading);

  /*
    Option A of "Today screen redesign" (owner, 2026-10-26), the theme's dashboard grid: the day in a
    sentence on the band, four figure cards rising over it, the day's work filtered by kind with
    the money and bookings taken beside it, then tomorrow's garments and the week ahead. It
    replaced option A of "Today screen options" (2026-10-23), whose counts strip and single list it
    keeps underneath: the same counts, the same work order, the same rows.
  */
  const chips = countChips(shows, (id) => dataFor(id).count);
  const figures = dayFigures(chips, shows, (id) => dataFor(id).count);
  const work = workList(cards, dataFor);
  const filters = workFilters(
    work.map(({ id, data: section }) => ({ id, count: section.count ?? section.rows.length })),
  );
  const filter = filters.some((each) => each.id === picked) ? picked : 'all';
  const shownWork = work.filter(({ id }) => inFilter(id, filter));
  const mayHandle = canAct(session, 'RentalOrder:Edit');
  const mayTake = canAct(session, 'RentalOrder:Create') && takenVisible;
  const maySell =
    hasFeature(session, 'Pos') &&
    canAct(session, 'SalesInvoice:Create') &&
    can(session, 'SalesInvoice:View');
  const tomorrow = tomorrowSummary(outRows, backRows);
  const byAccount = moneyInByAccount(position.data?.accounts ?? []);
  const week = weekAhead(ahead.data?.items ?? [], today);
  const weekMost = Math.max(1, ...week.map((day) => day.garments));
  const busiest = busiestLine(week);
  const takenNow = taken[period];

  const openChip = (id: CountChip['id']) => {
    if (id === 'workroom') {
      const first = (['inspection', 'alterations', 'cleaning'] as const).find(shows);
      const card = cards.find((candidate) => candidate.id === first);

      if (card) {
        onOpen(card.target, card.targetView);
      }

      return;
    }

    onOpen('bookings', id);
  };

  return (
    <>
      <PageHeading
        title={greeting(session.user.fullName)}
        eyebrow={[
          toDayjs(today)?.format('dddd, D MMMM YYYY') ?? formatDate(today),
          session.branch?.name,
        ]
          .filter(Boolean)
          .join(' · ')}
        subtitle={
          daySentence(chips) ||
          (session.branch?.name ? 'A quiet day so far.' : 'No branch selected')
        }
        overlap={figures.length > 0}
        actions={
          <>
            {maySell && (
              <Button icon="icon icon-shopping-bag" onClick={() => onOpen('pos', 'new')}>
                New sale
              </Button>
            )}
            {mayTake && (
              <Button
                variant="primary"
                icon="icon icon-plus"
                shortcut="create"
                onClick={() => onOpen('bookings', takeBookingIntent)}
              >
                New booking
              </Button>
            )}
          </>
        }
      />

      {figures.length > 0 && (
        <section
          aria-label="Today in figures"
          className="mb-4 grid grid-cols-1 gap-4 px-4 sm:grid-cols-2 xl:grid-cols-4"
        >
          {figures.map((figure) => (
            <button
              key={figure.id}
              type="button"
              onClick={() => openChip(figure.id)}
              aria-label={`${figure.label}: ${figure.count}. Open.`}
              className="ar-card flex cursor-pointer items-center justify-between gap-3 p-4 text-start hover:shadow-md lg:p-5"
            >
              <span className="min-w-0">
                <span className="text-default mb-1 block truncate text-[length:var(--scale-list-name)]">
                  {figure.label}
                </span>
                {dataFor(figure.id === 'workroom' ? 'inspection' : figure.id).loading ? (
                  <Skeleton.Button active size="small" style={{ width: 36, minWidth: 36 }} />
                ) : (
                  <span
                    className={`font-mono-nums block text-[28px] leading-tight font-bold ${
                      figure.attention ? 'text-warning' : 'text-gray-900'
                    }`}
                  >
                    {figure.count}
                  </span>
                )}
                <span className="text-default block truncate text-[length:var(--scale-list-meta)]">
                  {figure.hint}
                </span>
              </span>
              <span
                className={`flex size-11 shrink-0 items-center justify-center rounded-xl ${hueClasses[figureHue[figure.id]].tile}`}
                aria-hidden="true"
              >
                <i className={`${figureIcon[figure.id]} text-lg text-white`} />
              </span>
            </button>
          ))}
        </section>
      )}

      {cards.length === 0 ? (
        /*
          Signed in and holding nothing — the seeded `nikhil` is exactly this account. It lands here
          rather than on a 404 because this screen carries no `requires`, so it is the one place such
          a person can be left sitting.
        */
        <Card>
          <EmptyState
            icon="icon icon-lock"
            title="Nothing has been shared with you yet"
            hint="Your sign-in works, but no role has been assigned to your account, so there are no screens to show. Ask whoever administers AttireRent at your company to give you one."
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-12 items-start gap-4">
            <Card flush className="col-span-12 overflow-hidden pb-2 xl:col-span-8">
              <div className="border-border-color flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
                <div>
                  <h2 className="mb-0 text-[17px] font-bold text-gray-900">Today&apos;s work</h2>
                  <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                    In the order the day is worked: late first, then the counter, then the workroom.
                  </p>
                </div>
                {can(session, 'Availability:View') && (
                  <Button icon="icon icon-calendar-days" onClick={() => onOpen('calendar')}>
                    Calendar
                  </Button>
                )}
              </div>

              {filters.length > 1 && (
                <div
                  role="group"
                  aria-label="Show"
                  className="border-border-color flex flex-wrap gap-1.5 border-b px-5 py-3"
                >
                  {filters.map((each) => (
                    <button
                      key={each.id}
                      type="button"
                      aria-pressed={filter === each.id}
                      onClick={() => setPicked(each.id)}
                      className={`h-8 cursor-pointer rounded-full border px-3 text-[13px] ${
                        filter === each.id
                          ? 'border-primary bg-primary font-semibold text-white'
                          : 'border-border-color bg-white text-gray-900 hover:bg-light'
                      }`}
                    >
                      {each.label} · {each.count}
                    </button>
                  ))}
                </div>
              )}

              {shownWork.map(({ card, id, data: section }) => (
                <WorkSection
                  key={id}
                  id={id}
                  card={card}
                  data={section}
                  mayHandle={mayHandle}
                  onRow={(row) =>
                    opensBooking(id)
                      ? onOpen('bookings', undefined, undefined, Number(row.key))
                      : onOpen(card.target, card.targetView)
                  }
                  onAll={() => onOpen(card.target, card.targetView)}
                />
              ))}

              {/* Every queue empty is the commonest morning a shop has, and it is an answer. */}
              {work.length === 0 && !anyLoading && (
                <EmptyState
                  icon="icon icon-check-check"
                  title="Nothing needs doing yet"
                  hint="No collections, returns or jobs are outstanding at this branch today."
                />
              )}
              {work.length === 0 && anyLoading && (
                <p className="text-default px-5 py-6 text-[length:var(--scale-list-name)]">
                  Reading today&apos;s work…
                </p>
              )}
            </Card>

            <aside className="col-span-12 flex min-w-0 flex-col gap-4 xl:col-span-4">
              {(showsMoney('cash') || dueLine || showsMoney('outstanding')) && (
                <Card>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h2 className="mb-0 text-[15px] font-bold text-gray-900">Money today</h2>
                      {showsMoney('cash') && (
                        // The cash screen's own heading rule — "Cash in hand" only once every
                        // account has a float — imported, never restated.
                        <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                          {headlineLabel(confidenceOf(position.data))}
                        </p>
                      )}
                    </div>
                    {showsMoney('cash') && (
                      <button
                        type="button"
                        onClick={() => onOpen('cash')}
                        className="text-primary cursor-pointer text-[13px] font-semibold hover:underline"
                      >
                        Cash &amp; bank →
                      </button>
                    )}
                  </div>

                  {showsMoney('cash') && (
                    <>
                      <p className="mt-2 mb-0 font-serif text-[30px] font-semibold text-gray-900">
                        {position.isLoading
                          ? '…'
                          : position.isError
                            ? '—'
                            : rupees(position.data?.closing ?? 0)}
                      </p>
                      <div className="mt-3 grid grid-cols-2 gap-2.5">
                        <div className="bg-success-transparent rounded-[10px] px-3 py-2.5">
                          <p className="text-success mb-0 text-[12px]">Money in</p>
                          <p className="font-mono-nums mb-0 text-[17px] font-bold text-gray-900">
                            {rupees(moved.moneyIn)}
                          </p>
                        </div>
                        <div className="bg-warning-transparent rounded-[10px] px-3 py-2.5">
                          <p className="text-warning mb-0 text-[12px]">Money out</p>
                          <p className="font-mono-nums mb-0 text-[17px] font-bold text-gray-900">
                            {rupees(moved.moneyOut)}
                          </p>
                        </div>
                      </div>
                      {byAccount.length > 0 && (
                        <ul
                          aria-label="Where it came in"
                          className="mt-3.5 mb-0 flex flex-col gap-2 ps-0"
                        >
                          {byAccount.map((account) => (
                            <li key={account.id} className="list-none">
                              <span className="flex justify-between text-[12px] text-gray-900">
                                <span>{account.name}</span>
                                <span className="font-mono-nums font-semibold">
                                  {rupees(account.amount)}
                                </span>
                              </span>
                              <span
                                className="bg-light mt-1 block h-1.5 overflow-hidden rounded-full"
                                aria-hidden="true"
                              >
                                <span
                                  className="bg-primary block h-1.5 rounded-full"
                                  style={{ width: `${account.share}%` }}
                                />
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}

                  {showsMoney('outstanding') && receivables.data && (
                    <div className="border-border-color mt-3.5 flex flex-col gap-1.5 border-t pt-3 text-[13px]">
                      <p className="mb-0 flex justify-between gap-2">
                        <span className="text-default">Owed to the shop</span>
                        <span className="font-mono-nums font-semibold text-gray-900">
                          {rupees(receivables.data.total)}
                        </span>
                      </p>
                      {payNow > 0 && (
                        <p className="mb-0 flex justify-between gap-2">
                          <span className="text-default">To pay suppliers now</span>
                          <span className="font-mono-nums font-semibold text-gray-900">
                            {rupees(payNow)}
                          </span>
                        </p>
                      )}
                    </div>
                  )}

                  {dueLine && (
                    <div className="border-border-color mt-3 flex items-center justify-between gap-2 border-t pt-3">
                      <span
                        className={`text-[length:var(--scale-list-meta)] ${
                          dueLine.tone === 'warning' ? 'text-warning font-semibold' : 'text-default'
                        }`}
                      >
                        {dueLine.text}
                      </span>
                      <Button onClick={() => onOpen('expenses')}>Record</Button>
                    </div>
                  )}
                </Card>
              )}

              {takenVisible && (
                <Card>
                  <div className="flex items-center justify-between gap-2">
                    <h2 className="mb-0 text-[15px] font-bold text-gray-900">Bookings taken</h2>
                    <div role="group" aria-label="Period" className="flex gap-1">
                      {tradingPeriodLabels.map((each) => (
                        <button
                          key={each.id}
                          type="button"
                          aria-pressed={period === each.id}
                          onClick={() => setPeriod(each.id)}
                          className={`h-7 cursor-pointer rounded-md px-2.5 text-[12px] ${
                            period === each.id
                              ? 'bg-primary-transparent text-primary font-bold'
                              : 'text-gray-900 hover:bg-light'
                          }`}
                        >
                          {each.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="mt-2 mb-0 flex items-baseline gap-2.5">
                    <span className="font-serif text-[30px] font-semibold text-gray-900">
                      {takenNow.failed ? '—' : (takenNow.total ?? '…')}
                    </span>
                    <span className="text-default text-[13px]">
                      {takenWords[period](takenNow.total ?? 0)}
                    </span>
                  </p>
                </Card>
              )}
            </aside>
          </div>

          <div className="grid grid-cols-12 items-start gap-4">
            {/*
              Tomorrow, rendered even when empty — "nothing goes out tomorrow" is an answer worth
              reading in its usual place. Garments, never bookings; one still at the tailor is
              flagged amber and never dropped.
            */}
            {lists.length > 0 && (
              <Card flush className="col-span-12 overflow-hidden xl:col-span-7">
                <div className="border-border-color flex flex-wrap items-center justify-between gap-2.5 border-b px-5 py-4">
                  <div>
                    <h2 className="mb-0 text-[17px] font-bold text-gray-900">
                      Get ready for tomorrow
                    </h2>
                    <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                      {formatDate(dayAfterToday)} ·{' '}
                      {outTomorrow.isLoading || backTomorrow.isLoading ? 'Reading…' : tomorrow.line}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {lists.map((list) => (
                      <Button key={list.id} onClick={() => onOpen(list.target, list.targetView)}>
                        {list.id === 'pickupsTomorrow' ? 'Going out' : 'Coming back'}
                      </Button>
                    ))}
                  </div>
                </div>
                {tomorrow.chase > 0 && (
                  <p className="text-warning mb-0 px-5 pt-3 text-[length:var(--scale-list-meta)] font-semibold">
                    {tomorrow.chase === 1
                      ? '1 going out is still at the tailor — chase it today.'
                      : `${tomorrow.chase} going out are still at the tailor — chase them today.`}
                  </p>
                )}
                {outRows.length > 0 && (
                  <ul className="m-0 grid list-none grid-cols-1 gap-2.5 px-5 py-4 ps-5 sm:grid-cols-2 lg:grid-cols-3">
                    {outRows.slice(0, pullLimit).map((row) => (
                      <li
                        key={row.key}
                        className={`flex list-none items-center gap-2.5 rounded-xl border p-2.5 ${
                          row.flagged
                            ? 'bg-warning-transparent border-[color:var(--color-warning)]/30'
                            : 'border-border-color'
                        }`}
                      >
                        <span className="bg-light relative flex h-[42px] w-[34px] shrink-0 items-center justify-center overflow-hidden rounded-lg">
                          <i className="icon icon-shirt text-default text-sm" aria-hidden="true" />
                          {row.image && (
                            <img
                              src={row.image}
                              alt=""
                              className="absolute inset-0 size-full object-cover"
                              onError={(event) => {
                                event.currentTarget.style.display = 'none';
                              }}
                            />
                          )}
                        </span>
                        <span className="min-w-0">
                          <span className="font-mono-nums block truncate text-[12px] font-bold text-gray-900">
                            {row.primary}
                          </span>
                          <span
                            className={`block truncate text-[12px] ${
                              row.flagged ? 'text-warning font-semibold' : 'text-default'
                            }`}
                          >
                            {row.flagged ? 'At the tailor' : row.secondary}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {moreLabel(outRows.length, Math.min(outRows.length, pullLimit)) && (
                  <p className="text-default mb-0 px-5 pb-4 text-[length:var(--scale-list-meta)]">
                    {moreLabel(outRows.length, Math.min(outRows.length, pullLimit))}
                  </p>
                )}
              </Card>
            )}

            {shows('pickups') && (
              <Card className={`col-span-12 ${lists.length > 0 ? 'xl:col-span-5' : ''}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h2 className="mb-0 text-[15px] font-bold text-gray-900">
                      The next seven days
                    </h2>
                    <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                      Garments going out each day
                    </p>
                  </div>
                  {can(session, 'Availability:View') && (
                    <button
                      type="button"
                      onClick={() => onOpen('calendar')}
                      className="text-primary cursor-pointer text-[13px] font-semibold hover:underline"
                    >
                      Calendar →
                    </button>
                  )}
                </div>
                <ol
                  aria-label="Garments going out on each of the next seven days"
                  className="m-0 mt-4 grid h-[150px] list-none grid-cols-7 items-end gap-2 ps-0"
                >
                  {week.map((day) => {
                    const top = day.garments > 0 && day.garments === weekMost;

                    return (
                      <li
                        key={day.date}
                        className="flex h-full list-none flex-col items-center justify-end gap-1.5"
                        aria-label={`${day.day}: ${day.garments} garments`}
                      >
                        <span className="text-[12px] font-bold text-gray-900">
                          {ahead.isLoading ? '…' : day.garments}
                        </span>
                        <span
                          className={`block w-full max-w-[30px] rounded-t-md rounded-b-sm ${
                            top ? 'bg-primary' : 'bg-primary-transparent'
                          }`}
                          style={{
                            height: `${Math.max(4, Math.round((day.garments / weekMost) * 100))}px`,
                          }}
                          aria-hidden="true"
                        />
                        <span
                          className={`text-[12px] ${top ? 'font-bold text-gray-900' : 'text-default'}`}
                        >
                          {day.day}
                        </span>
                      </li>
                    );
                  })}
                </ol>
                <p className="text-default mt-3 mb-0 text-[length:var(--scale-list-meta)]">
                  {ahead.isError
                    ? 'The week ahead could not be read.'
                    : (busiest ?? 'Nothing goes out in the next seven days yet.')}
                </p>
              </Card>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const rupees = (value: number) => `₹${formatAmount(value)}`;

/** Each figure card's tile — one hue each, never warning or danger (CLAUDE.md, colour identity). */
const figureHue: Record<CountChip['id'], Hue> = {
  pickups: 'primary',
  returns: 'teal',
  overdue: 'indigo',
  workroom: 'purple',
};

const figureIcon: Record<CountChip['id'], string> = {
  pickups: 'icon icon-arrow-up-right',
  returns: 'icon icon-arrow-down-left',
  overdue: 'icon icon-clock',
  workroom: 'icon icon-scissors',
};

const takenWords: Record<keyof TradingPeriods, (count: number) => string> = {
  today: (count) => (count === 1 ? 'booking taken today' : 'bookings taken today'),
  week: (count) => (count === 1 ? 'booking since Monday' : 'bookings since Monday'),
  month: (count) => (count === 1 ? 'booking this month' : 'bookings this month'),
};

interface CardData {
  count?: number | undefined;
  rows: TodayRow[];
  loading: boolean;
  failed: boolean;
}

/**
 * One volume figure and how its query went. `total` is explicitly `| undefined`
 * (`exactOptionalPropertyTypes`), the distinction rendered as an em dash rather than a zero.
 */
interface TakenFigure {
  total?: number | undefined;
  loading: boolean;
  failed: boolean;
}

/**
 * One section of the work list — its heading, its rows, and on each row the button for the step it
 * leads to. Late rows sit on the reserved amber wash. A booking's button opens that booking, where
 * the step is next; a workroom row's opens its queue.
 */
function WorkSection({
  id,
  card,
  data,
  mayHandle,
  onRow,
  onAll,
}: {
  id: WorkSectionId;
  card: TodayCard;
  data: CardData;
  /** May move a booking on (`RentalOrder:Edit`); otherwise its button only opens it. */
  mayHandle: boolean;
  onRow: (row: TodayRow) => void;
  onAll: () => void;
}) {
  const section = workSections[id];
  const late = section.group === 'late';
  const count = data.count ?? data.rows.length;
  const shown = data.rows.slice(0, rowLimit);
  const more = moreLabel(count, shown.length);
  const action = opensBooking(id) && !mayHandle ? 'Open' : section.action;

  return (
    <section className="mt-3">
      <p
        className={`mb-1 px-5 text-[11px] font-bold tracking-[0.12em] uppercase ${
          late ? 'text-warning' : 'text-default'
        }`}
      >
        {section.heading} · {count}
      </p>
      <ul className="ps-0">
        {shown.map((row) => (
          <li
            key={row.key}
            className={`border-border-color flex list-none items-center gap-3 border-b px-5 py-2.5 ${
              late ? 'bg-warning-transparent' : ''
            }`}
          >
            <span className="bg-primary-transparent relative flex h-[52px] w-[42px] shrink-0 items-center justify-center overflow-hidden rounded-lg">
              <i className={`${card.icon} text-base text-gray-900`} aria-hidden="true" />
              {row.image && (
                <img
                  src={row.image}
                  alt=""
                  className="absolute inset-0 size-full object-cover"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                  }}
                />
              )}
            </span>

            <span className="min-w-0 flex-1">
              <span className="text-title block truncate text-[length:var(--scale-list-name)] font-semibold">
                {row.primary}
              </span>
              <span className="text-default block truncate text-[length:var(--scale-list-meta)]">
                {[
                  row.secondary,
                  row.trailingDate
                    ? `${row.trailingLabel ?? 'Due'} ${formatDate(row.trailingDate)}`
                    : row.trailing
                      ? // A count reads "3 garments"; a reference reads "Booking BK-…".
                        /^\d+$/.test(row.trailing)
                        ? `${row.trailing} ${row.trailingLabel ?? ''}`.trim()
                        : `${row.trailingLabel ?? ''} ${row.trailing}`.trim()
                      : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>

            {row.flagged && !late && (
              <StatusBadge tone={card.flag?.tone ?? 'negative'}>
                {card.flag?.label ?? 'Late'}
              </StatusBadge>
            )}

            <Button
              variant={id === 'pickups' && mayHandle ? 'primary' : 'secondary'}
              onClick={() => onRow(row)}
            >
              {action}
            </Button>
          </li>
        ))}
      </ul>
      {more && (
        <p className="text-default mb-0 flex items-center justify-between gap-2 px-5 pt-2 text-[length:var(--scale-list-meta)]">
          {more}
          <button
            type="button"
            onClick={onAll}
            className="text-primary cursor-pointer font-semibold hover:underline"
          >
            View all
          </button>
        </p>
      )}
    </section>
  );
}

/**
 * Named rather than "Dashboard", because the person reading this is standing at a counter and the
 * screen is about their morning. The first name only — the full one is on the account menu, and
 * "Good morning, Rakesh Sharma" reads like a letter from the bank.
 */
function greeting(fullName: string): string {
  const first = fullName.trim().split(/\s+/)[0];
  const hour = new Date().getHours();
  const part = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return first ? `${part}, ${first}` : part;
}
